import { usePublicOffers, listenerOffersFree, useFreeSessionIneligible } from '../../../components/FirstSessionFree/FirstSessionFree';
import { photosFirst } from '../../../util/listingReviews';
import { listingCardPhotoUrl } from '../../../util/listingCardPhoto';
import MatchMeButton from '../../../components/MatchMeButton/MatchMeButton';
import React from 'react';
import { useHiddenListingIds, withoutHidden } from '../../../util/hiddenListings';
import { useSelector } from 'react-redux';
import classNames from 'classnames';

import { FormattedMessage } from '../../../util/reactIntl';
import { propTypes } from '../../../util/types';
import { excludeOwnDiscoveryListings } from '../../../util/ownDiscoveryListings';
import { NamedLink, PaginationLinks } from '../../../components';
import KeywordSearchBar from '../../../components/KeywordSearchBar/KeywordSearchBar';
import { SmartSearchEmpty } from '../NoSearchResultsMaybe/NoSearchResultsMaybe';
import { SKILL_CATEGORIES } from '../../../util/sweetSkills';
import usePresenceMap from '../../../util/usePresenceMap';
import useReviewStats from '../../../util/useReviewStats';
import { isListingOnline } from '../../../util/presence';
import { stringify } from '../../../util/urlHelpers';
import {
  AVAILABLE_NOW_PARAM,
  availableNowParamForAnyTime,
  filterListingsByAvailableNow,
  isAvailableNowFirstEnabled,
  isAvailableNowOnlyFilter,
  sortSearchListings,
} from '../../../util/listingReviews';
import { filterListingsByOfferedFormat } from '../../../util/landingSearch';
import {
  filterListingsByTopics,
  parseTopicsParam,
  rankListingsByOverlap,
} from '../../../util/sessionTopics';
import {
  MatchRow,
} from '../../../components/TalkAbout/TalkAbout';
import {
  LISTENING_STYLES_PARAM,
  filterListingsByListeningStyles,
  parseListeningStylesParam,
} from '../../../util/listeningStyles';

import css from './SearchResultsPanel.module.css';
import TopicAccordion from '../../../components/TopicAccordion/TopicAccordion';
import ListenerBrowseCard from '../../../components/ListenerBrowseCard/ListenerBrowseCard';
import dirCss from '../../../components/TalkAbout/TopicDirectory.module.css';
import PopularTopics from '../../../components/TalkAbout/PopularTopics';
import { PathHeart } from '../../../components/TalkAbout/TopicHeart';
import { useHistory } from 'react-router-dom';
import { parsePathsParam, pathsParam, listingMatchesPaths, rememberTopicPath, listingAtOrBelow, listingMatchesKeywords, wordStartsIn, rankListingsByPaths, pathsFromListing, pathLabels, childrenOf, CATEGORIES, isFeaturedPath, nodeFor } from '../../../util/topicPaths';
import { setLiveResultCount } from '../../../util/liveResultCount';
import {
  GENDER_PARAM,
  filterListingsByReveals,
} from '../../../util/profileReveals';

/**
 * SearchResultsPanel component
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} [props.rootClassName] - Custom class that extends the default class for the root element
 * @param {Array<propTypes.listing>} props.listings - The listings
 * @param {propTypes.pagination} props.pagination - The pagination
 * @param {Object} props.search - The search
 * @param {Function} props.setActiveListing - The function to handle the active listing
 * @param {boolean} [props.isMapVariant] - Whether the map variant is enabled
 * @returns {JSX.Element}
 */
// Topic search "dropdown plus chips" (Sam, Oct 20 2026): one Pick a topic dropdown; Popular chips stay under it.
const DROPDOWN_SKIP = ['life-after-55'];
export const TopicDropdown = ({ value, onPick }) => {
  const label = value ? pathLabels(value).slice(-1)[0] : '';
  const groups = CATEGORIES.filter(c => !DROPDOWN_SKIP.includes(c.id) && isFeaturedPath(c.id));
  return (
    <div className={classNames(css.topicDd, { [css.topicDdOn]: !!value })} data-topic-dropdown={value || 'none'}>
      <span className={css.topicDdText} aria-hidden="true">
        {value ? <small>Topic</small> : null}
        {value ? label : 'Pick a topic'}
      </span>
      {value ? (
        <button type="button" className={css.topicDdClear} aria-label={`Clear topic ${label}`} onClick={() => onPick(null)} data-topic-clear="true">✕</button>
      ) : (
        <span className={css.topicDdCaret} aria-hidden="true">▾</span>
      )}
      <select
        className={css.topicDdSelect}
        aria-label="Pick a topic"
        value={value && nodeFor(value) ? value : ''}
        onChange={e => onPick(e.target.value || null)}
      >
        <option value="">Pick a topic</option>
        {groups.map(c => (
          <optgroup key={c.id} label={c.label}>
            <option value={c.id}>All of {c.label}</option>
            {(c.children || []).filter(ch => isFeaturedPath(`${c.id}/${ch.id}`)).map(ch => (
              <option key={ch.id} value={`${c.id}/${ch.id}`}>{ch.label}</option>
            ))}
          </optgroup>
        ))}
      </select>
    </div>
  );
};

// Search v2 (Sam, Oct 20 2026): 4 broad categories › subcategory › listeners.
export const SEARCH_GROUPS = [
  { id: 'everyday', label: 'Everyday Talk & Company', cats: ['just-talk', 'new-in-town', 'life-after-55'] },
  { id: 'life', label: 'Life, Love & Family', cats: ['life', 'women-supporting-women', 'womens-issues'] },
  { id: 'growth', label: 'Work & Growth', cats: ['mentorship-life-guidance', 'work'] },
  { id: 'fun', label: 'Fun & Interests', cats: ['sports', 'shows', 'world', 'hobbies'] },
  { id: 'health', label: 'Health & Identity', cats: ['health', 'gender-identity-sexuality'] },
];
export const groupFor = id => SEARCH_GROUPS.find(g => g.id === id) || null;
const groupOfPath = p => p ? SEARCH_GROUPS.find(g => g.cats.includes(String(p).split('/')[0])) || null : null;
export const CategoryDropdowns = ({ groupId, path, onGroup, onPath }) => {
  const g = groupFor(groupId) || groupOfPath(path);
  const cats = g ? g.cats.filter(isFeaturedPath).map(id => CATEGORIES.find(c => c.id === id)).filter(Boolean) : [];
  return (
    <div className={css.ddRow} data-category-dropdowns={g ? g.id : 'none'}>
      <label className={css.ddLabel}>
        <span>1. Pick a category</span>
        <select className={css.ddSelect} value={g ? g.id : ''} data-category-select="true" onChange={e => onGroup(e.target.value || null)}>
          <option value="">Choose a category</option>
          {SEARCH_GROUPS.map(x => <option key={x.id} value={x.id}>{x.label}</option>)}
        </select>
      </label>
      {g ? (
        <label className={css.ddLabel} data-topic-dropdown={path || 'none'}>
          <span>2. Pick a subcategory</span>
          <select className={css.ddSelect} value={path && nodeFor(path) ? path : ''} data-subcategory-select="true" onChange={e => onPath(e.target.value || null, g)}>
            <option value="">Choose a subcategory</option>
            {cats.map(c => (
              <optgroup key={c.id} label={c.label}>
                <option value={c.id}>All of {c.label}</option>
                {(c.children || []).filter(ch => isFeaturedPath(`${c.id}/${ch.id}`)).map(ch => (
                  <option key={ch.id} value={`${c.id}/${ch.id}`}>{ch.label}</option>
                ))}
              </optgroup>
            ))}
          </select>
        </label>
      ) : null}
    </div>
  );
};

// Inline "Browse topics" expander (Sam, Oct 21 2026): tick any topics, Search matches any.
export const TopicSearchPicker = ({ activePaths, onSearch, startOpen = false }) => {
  const [open, setOpen] = React.useState(startOpen);
  const [draft, setDraft] = React.useState(activePaths);
  const key = activePaths.join('|');
  React.useEffect(() => setDraft(activePaths), [key]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className={css.topicPicker} data-topic-picker={open ? 'open' : 'closed'}>
      <button type="button" className={css.topicPickerToggle} aria-expanded={open} onClick={() => setOpen(!open)} data-browse-topics-toggle="true">
        <span>{open ? '−' : '+'}</span> {!open && draft.length ? `${draft.length === 1 ? '1 topic' : `${draft.length} topics`} picked · Edit` : 'Browse topics'}
      </button>
      {open ? (
        <div className={css.topicPickerBody}>
          <p className={css.topicPickerHint}>Tick everything you’d love to talk about. We’ll show listeners who share any of them.</p>
          <TopicAccordion value={draft} onChange={setDraft} idPrefix="search" />
          <div className={css.topicPickerBar}>
            <span data-topic-picked={draft.length}>{draft.length === 1 ? '1 topic picked' : `${draft.length} topics picked`}</span>
            <button type="button" className={css.topicPickerClear} disabled={!draft.length} onClick={() => setDraft([])} data-topic-clear="true">Clear</button>
            <button type="button" className={css.topicPickerGo} disabled={!draft.length} onClick={() => {
              setOpen(false);
              onSearch(draft);
              // Results sit below the picker: bring the "We found…" heading into view.
              [350, 1200].forEach(ms =>
                setTimeout(() => {
                  const el = document.querySelector('[data-inline-matches], [data-topic-fallback], [data-results-bar]');
                  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }, ms)
              );
            }} data-topic-search="true">Search</button>
          </div>
        </div>
      ) : null}
    </div>
  );
};
export const BROWSE_SORTS = [['az', 'A–Z'], ['interests', 'Interests'], ['online', 'Online now'], ['reviews', 'Top reviews']];

// Sweet Treat chip (Sam, Oct 19 2026): ?sweetTreat=1 shows listeners giving the free first session.
export const SWEET_TREAT_PARAM = 'sweetTreat';
// Warm results headline (Sam, Oct 19): "💕 We found 4 listeners who love Football!"
export const resultsHeadline = ({ count, topic, nowOnly }) => {
  if (!count) return nowOnly ? 'No one is available right now. Try Any time or another topic.' : 'No matches yet. Try Any time or another topic.';
  const who = count === 1 ? '1 listener' : `${count} listeners`;
  const tail = nowOnly ? ' online' : '';
  return topic ? `💕 We found ${who} who love${count === 1 ? 's' : ''} ${topic}${tail}!` : `💕 We found ${who}${tail}!`;
};

const isSweetTreatOn = search => ['1', 'true'].includes(String(search?.[SWEET_TREAT_PARAM] || search?.pub_sweetTreat || ''));

const SearchResultsPanel = props => {
  const {
    className,
    rootClassName,
    listings = [],
    pagination,
    search,
    isMapVariant = true,
    listingTypeParam,
    intl,
  } = props;
  const classes = classNames(rootClassName || css.root, className);
  const pageName = listingTypeParam ? 'SearchPageWithListingType' : 'SearchPage';
  const currentUser = useSelector(state => state.user?.currentUser);
  const currentUserOwnListing = useSelector(state => state.user?.currentUserOwnListing);
  const hiddenIds = useHiddenListingIds();
  const discoveryListings = withoutHidden(
    excludeOwnDiscoveryListings(listings, {
      currentUser,
      currentUserOwnListing,
    }),
    hiddenIds
  );
  // Woman listener / age range (Sam, Oct 17): only listeners who opted in match.
  const sweetOffersRaw = usePublicOffers();
  const freeIneligible = useFreeSessionIneligible();
  // Ineligible talkers see no free-session banner, chip or filter (Sam, Oct 21).
  const sweetOffers = freeIneligible ? null : sweetOffersRaw;
  const sweetOn = isSweetTreatOn(search) && !!sweetOffers?.live;
  const listingsForFormatAll = filterListingsByReveals(
    filterListingsByListeningStyles(
      filterListingsByTopics(
        filterListingsByOfferedFormat(discoveryListings, search?.format),
        search?.topics
      ),
      search?.[LISTENING_STYLES_PARAM]
    ),
    search
  );
  const listingsForFormat = sweetOn
    ? listingsForFormatAll.filter(l => listenerOffersFree(sweetOffers, l?.author?.id?.uuid))
    : listingsForFormatAll;
  const womanOn = search?.[GENDER_PARAM] === 'woman';
  const ageOn = ''; // age filter removed (Sam, Oct 21 2026)
  const listingIds = listingsForFormat.map(l => l?.id?.uuid).filter(Boolean);
  const {
    map: presenceByListingId,
    pausedMap = {},
    busyMap = {},
    liveMap = {},
    nextAvailableMap = {},
  } = usePresenceMap(listingIds, { includeAllOnline: false });
  // Oct 27 2026 (Sam): Available Now with or without a live heartbeat (Talk Now warns instead).
  const liveOnline = l => isListingOnline(l.id.uuid, presenceByListingId, l);
  const awayFor = () => false;
  void liveMap;
  const reviewStatsByListingId = useReviewStats(listingIds);
  const onlineOnly = isAvailableNowOnlyFilter(search);
  // Missing presence is offline. Do not wait on a fetch or Online now hangs
  // on “Checking who is available now…” with every seed card hidden.
  const listingsForAvailability = filterListingsByAvailableNow(
    listingsForFormat,
    presenceByListingId,
    search
  );
  const activePaths = parsePathsParam(search?.paths);
  const sortedListingsAll = sortSearchListings(listingsForAvailability, {
    statsMap: reviewStatsByListingId,
    presenceMap: presenceByListingId,
    sortKey: search?.sort,
    availableNowFirst: isAvailableNowFirstEnabled(search),
  });
  const [browseAt] = React.useState('');
  // Match = the listener chose this topic, something inside it, or a broader topic that contains it
  // (legacy flat topics included). With no match, the closest parent topic is shown, labeled.
  // Home search sends ?q= (Sam, Oct 15); older links use ?keywords=.
  const searchInProgressSel = useSelector(state => state.SearchPage?.searchInProgress);
  const kwTerm = search?.keywords || search?.q || '';
  // Name match first (Oct 20): typing a listener's display name puts them on top.
  const kwLower = String(kwTerm || '').trim().toLowerCase();
  const nameHit = l => {
    if (!kwLower) return false;
    const n = [l?.author?.attributes?.profile?.displayName, l?.attributes?.title].filter(Boolean).join(' ').toLowerCase();
    return n.includes(kwLower) || kwLower.split(/\s+/).every(w => wordStartsIn(n, w));
  };
  const kwMatched = sortedListingsAll
    .filter(l => listingAtOrBelow(l, browseAt))
    .filter(l => listingMatchesKeywords(l, kwTerm));
  const kwListings = kwLower
    ? [...kwMatched.filter(nameHit), ...kwMatched.filter(l => !nameHit(l))]
    : kwMatched;
  const browseSort = BROWSE_SORTS.some(([k]) => k === search?.bsort) ? search.bsort : 'az';
  const myPaths = currentUser?.attributes?.profile?.publicData?.interestedTopicPaths || [];
  const nameOf = l => String(l?.author?.attributes?.profile?.displayName || l?.attributes?.title || '').toLowerCase();
  const applyBrowseSort = rows => {
    const az = (a, b) => nameOf(a).localeCompare(nameOf(b));
    const st = l => reviewStatsByListingId[l?.id?.uuid] || { average: 0, count: 0 };
    const shared = l => { const mine = pathsFromListing(l); return myPaths.length ? myPaths.filter(p => listingMatchesPaths(l, [p])).length * 1000 + mine.length : mine.length; };
    const on = l => (liveOnline(l) ? 1 : 0);
    const cmp = {
      az,
      interests: (a, b) => shared(b) - shared(a) || az(a, b),
      online: (a, b) => on(b) - on(a) || az(a, b),
      reviews: (a, b) => st(b).average - st(a).average || st(b).count - st(a).count || az(a, b),
    }[browseSort];
    // Default Browse order (talker funnel, Oct 21 2026, TEST): Available now first,
    // then the strongest profiles (photo, reviews, a full About), then A–Z.
    if (!search?.bsort) {
      const hasPhoto = l => {
        try {
          return listingCardPhotoUrl(l) ? 1 : 0;
        } catch (e) {
          return 0;
        }
      };
      const aboutLen = l => String(l?.attributes?.description || '').length;
      const strength = l =>
        hasPhoto(l) * 40 +
        Math.min(st(l).count, 10) * 4 +
        (st(l).average || 0) * 2 +
        (aboutLen(l) >= 200 ? 15 : aboutLen(l) >= 80 ? 8 : 0) +
        Math.min(shared(l), 3);
      return [...rows].sort((a, b) => on(b) - on(a) || strength(b) - strength(a) || az(a, b));
    }
    const sorted = [...rows].sort(cmp);
    return sorted;
  };
  const filteredListings = activePaths.length
    ? kwListings.filter(l => listingMatchesPaths(l, activePaths))
    : kwListings;
  const firstActive = activePaths[0] || null;
  const activeGroup = groupFor(search?.cat);
  const groupAll = !!activeGroup && activePaths.length > 1;
  // Three entry points (Sam, Oct 15): search | topics | listeners.
  const MODES = ['search', 'topics', 'listeners'];
  const mode = MODES.includes(search?.mode)
    ? search.mode
    : activePaths.length || search?.view === 'topics'
    ? 'topics'
    : kwTerm
    ? 'search'
    : 'listeners';
  const searchSettled = !searchInProgressSel;
  const sortedListings = mode === 'listeners' && !kwLower ? applyBrowseSort(filteredListings) : filteredListings;
  const topicMatches = (() => {
    const words = String(kwTerm).toLowerCase().split(/\s+/).filter(w => w.length >= 2);
    if (mode !== 'search' || !words.length) return [];
    const out = [];
    const walk = at =>
      childrenOf(at).forEach(n => {
        const hay = [n.label, ...(n.keywords || [])].join(' ').toLowerCase();
        if (words.every(w => wordStartsIn(hay, w))) out.push(n.path);
        walk(n.path);
      });
    walk('');
    return out.slice(0, 10);
  })();
  // Keyword search: carry the best matching topic into booking (Sam, Oct 15).
  const topTopicMatch = topicMatches[0] || null;
  React.useEffect(() => {
    if (topTopicMatch && !activePaths.length) rememberTopicPath(topTopicMatch);
  }, [topTopicMatch]); // eslint-disable-line react-hooks/exhaustive-deps
  const wantsTopics = search?.view === 'topics';
  React.useEffect(() => {
    if (!wantsTopics || typeof document === 'undefined') return;
    const el = document.getElementById('browse-topics');
    if (el) window.setTimeout(() => el.scrollIntoView({ block: 'start', behavior: 'smooth' }), 300);
  }, [wantsTopics]);
  const nearest = (() => {
    if (!firstActive || sortedListings.length) return null;
    const segs = firstActive.split('/');
    for (let i = segs.length - 1; i >= 1; i--) {
      const parent = segs.slice(0, i).join('/');
      const rows = kwListings.filter(l => listingAtOrBelow(l, parent) || listingMatchesPaths(l, [parent]));
      if (rows.length) return { parent, label: pathLabels(parent).slice(-1)[0], rows };
    }
    return null;
  })();
  const emptyLabel = firstActive ? pathLabels(firstActive).slice(-1)[0] : null;
  const liveCount = sortedListings.length;
  React.useEffect(() => {
    setLiveResultCount(liveCount);
    return () => setLiveResultCount(null);
  }, [liveCount]);

  const talkerTopics = currentUser?.attributes?.profile?.publicData?.interestedTopics;
  const matchRows = rankListingsByPaths(
    discoveryListings,
    currentUser?.attributes?.profile?.publicData?.interestedTopicPaths,
    presenceByListingId,
    8,
    () => rankListingsByOverlap(discoveryListings, talkerTopics, presenceByListingId, 8)
  );
  const activeTopics = parseTopicsParam(search?.topics);
  const history = useHistory();
  const firstPath = activePaths[0] || null;
  React.useEffect(() => {
    if (firstPath) rememberTopicPath(firstPath);
  }, [firstPath]);

  // Search mode with no query: no cards, no count/sort (Sam, Oct 15).
  const searchEmpty = mode === 'search' && !String(kwTerm).trim() && !activePaths.length;
  // A name search reads "We found 1 listener!", not "who loves Maya" (Oct 20).
  const searchLabel = kwListings.some(nameHit) ? '' : String(kwTerm).trim() || (activePaths.length > 1 ? '' : groupAll ? activeGroup.label : firstActive ? pathLabels(firstActive).slice(-1)[0] : '');
  const freeCount = sweetOffers?.live ? listingsForFormatAll.filter(l => listenerOffersFree(sweetOffers, l?.author?.id?.uuid)).length : 0;
  const resultsBar = searchEmpty ? null : (
    <>
    {mode === 'listeners' ? (
      <MatchMeButton
        listings={sortedListings}
        presenceMap={presenceByListingId}
        busyMap={busyMap}
        pausedMap={pausedMap}
        reviewStats={reviewStatsByListingId}
      />
    ) : null}
    <div className={css.resultsBar} data-results-bar="true">
      {sweetOffers?.live && freeCount && !freeIneligible ? (
        <button
          type="button"
          className={classNames(css.freeBanner, { [css.freeBannerOn]: sweetOn })}
          aria-pressed={sweetOn}
          data-free-banner={sweetOn ? 'on' : 'off'}
          onClick={() => history.push(`/s?${stringify({ ...search, [SWEET_TREAT_PARAM]: sweetOn ? undefined : '1', pub_sweetTreat: undefined, page: undefined })}`)}
        >
          <span className={css.freeBannerGift}>🎁</span>
          <span className={css.freeBannerText}>
            <strong>{sweetOn ? 'Showing listeners with a free first session' : `${freeCount} listeners offer a free first session`}</strong>
            <span>{sweetOn ? 'Tap to see everyone again' : 'Your first 15 minutes are free. Tap to show only these listeners.'}</span>
          </span>
          <span className={css.freeBannerSwitch} aria-hidden="true">{sweetOn ? 'On' : 'Off'}</span>
        </button>
      ) : null}
      <p className={classNames(css.resultsCount, css.resultsHeadline)} role="status" aria-live="polite" data-results-count={liveCount}>
        {resultsHeadline({ count: liveCount, topic: searchLabel, nowOnly: isAvailableNowOnlyFilter(search) })}
      </p>
      <div className={css.resultsAvail} role="group" aria-label="Availability">
        {[['any', 'Any time', '0'], ['now', 'Online', 'only']].map(([k, label, v]) => {
          const on = k === 'now' ? isAvailableNowOnlyFilter(search) : !isAvailableNowOnlyFilter(search);
          return (
            <button
              key={k}
              type="button"
              className={classNames(css.resultsPill, { [css.resultsPillOn]: on })}
              aria-pressed={on}
              data-results-avail={k}
              onClick={() => history.push(`/s?${stringify({ ...search, [AVAILABLE_NOW_PARAM]: v, page: undefined })}`)}
            >
              {label}
            </button>
          );
        })}
      </div>
      <div className={css.listenerFilters} role="group" aria-label="Listener" data-listener-filters="true">
        <button
          type="button"
          className={classNames(css.resultsPill, css.listenerPill, { [css.listenerPillOn]: womanOn })}
          aria-pressed={womanOn}
          data-filter-woman={womanOn ? 'on' : 'off'}
          onClick={() =>
            history.push(`/s?${stringify({ ...search, [GENDER_PARAM]: womanOn ? undefined : 'woman', page: undefined })}`)
          }
        >
          {womanOn ? '✓ ' : ''}Woman listener
        </button>
        {sweetOffers?.live ? (
          <button
            type="button"
            className={classNames(css.resultsPill, css.listenerPill, css.freePill, { [css.listenerPillOn]: sweetOn, [css.freePillOn]: sweetOn })}
            aria-pressed={sweetOn}
            data-filter-sweet-treat={sweetOn ? 'on' : 'off'}
            onClick={() =>
              history.push(
                `/s?${stringify({ ...search, [SWEET_TREAT_PARAM]: sweetOn ? undefined : '1', pub_sweetTreat: undefined, page: undefined })}`
              )
            }
          >
            {sweetOn ? '✓ ' : '🎁 '}Free first session
          </button>
        ) : null}
        {/* Age filter removed (Sam, Oct 21 2026: no ages). Old ?ageRange= links are ignored. */}
      </div>
      {mode === 'listeners' && !kwLower ? (
        <label className={css.resultsSort}>
          <span>Sort by</span>
          <select value={browseSort} data-browse-sort="true" onChange={e => history.push(`/s?${stringify({ ...search, bsort: e.target.value === 'az' ? undefined : e.target.value, page: undefined })}`)}>
            {BROWSE_SORTS.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
          </select>
        </label>
      ) : (
        <label className={css.resultsSort}>
          <span>Sort</span>
          <select
            value={search?.sort === 'ranking' ? 'ranking' : 'recommended'}
            data-results-sort="true"
            onChange={e =>
              history.push(`/s?${stringify({ ...search, sort: e.target.value === 'recommended' ? undefined : e.target.value, page: undefined })}`)
            }
          >
            <option value="recommended">Recommended</option>
            <option value="ranking">Highest rated</option>
          </select>
        </label>
      )}
    </div>
    </>
  );

  const paginationLinks =
    !onlineOnly && pagination && pagination.totalPages > 1 ? (
      <PaginationLinks
        className={css.pagination}
        pageName={pageName}
        pagePathParams={{ listingType: listingTypeParam }}
        pageSearchParams={search}
        pagination={pagination}
        aria-label={intl.formatMessage({ id: 'SearchResultsPanel.screenreader.pagination' })}
      />
    ) : null;


  // Clean Browse (Sam, Oct 22 2026, TEST): no tabs, chips, promo or headings here;
  // the page shows one heading and one filter row, then these results.
  if (mode === 'listeners' && !kwLower && !activePaths.length) {
    return (
      <div className={classes} data-clean-browse="true">
        {isSweetTreatOn(search) ? (
          freeIneligible ? (
            <p className={css.freeNote} role="status" data-free15-note="used">
              💗 It looks like you’ve already used your free first session, so the usual prices apply. Every listener here is still happy to talk.
            </p>
          ) : sweetOn ? (
            <p className={css.freeTopBanner} role="status" data-free15-banner="true">
              🎁 Your first 15 minutes are free. Pick a listener to start.
            </p>
          ) : null
        ) : null}
        {sortedListings.length ? (
          <ul className={classNames(css.inlineList, css.browseGrid)} id="browse-inline-results">
            {sortedListings.map(l => (
              <li key={l.id.uuid} className={css.resultItem}>
                <ListenerBrowseCard
                  listing={l}
                  reviewStats={reviewStatsByListingId[l.id.uuid]}
                  online={liveOnline(l)}
                  away={awayFor(l)}
                  sessionBusy={!!busyMap[l.id.uuid]}
                  startNowPaused={!!pausedMap[l.id.uuid]}
                  nextAvailableAt={nextAvailableMap[l.id.uuid] || null}
                />
              </li>
            ))}
          </ul>
        ) : (
          <p className={css.topicEmpty} role="status" data-clean-empty="true">
            No one matches these filters right now. Try another format or turn off Online.
          </p>
        )}
        {paginationLinks}
      </div>
    );
  }

  return (
    <div className={classes}>
      <nav className={css.modeTabs} aria-label="Ways to find a listener" data-mode-tabs="true">
        {[
          ['search', 'Search', 'Search every listener by name, about or topic'],
          ['topics', 'Browse topics', 'Tick topics you love, then search'],
          ['listeners', 'Browse all listeners', 'Sort by A–Z, interests, online now or reviews'],
        ].map(([m, label, sub]) => (
          <a
            key={m}
            href={`/s?mode=${m}`}
            className={classNames(css.modeTab, { [css.modeTabOn]: mode === m })}
            aria-current={mode === m ? 'page' : undefined}
            data-mode-tab={m}
          >
            <span className={css.modeTabLabel}>{label}</span>
            <span className={css.modeTabSub}>{sub}</span>
          </a>
        ))}
      </nav>
      {mode !== 'topics' ? (
      <div className={css.filtersCompact} data-browse-search="true" data-browse-mode={mode}>
        {mode === 'listeners' ? <h2 className={dirCss.heading}>Browse all listeners</h2> : null}
        {mode === 'search' ? (
          <div className={css.findPerson} data-find-person="true">
            <h2 className={css.findPersonTitle}>Find Your Person</h2>
            <p className={css.findPersonSub}>What would you like to talk about?</p>
          </div>
        ) : null}
        <KeywordSearchBar search={{ ...search, keywords: kwTerm || undefined, q: undefined, mode }} pageName={pageName} listingTypeParam={listingTypeParam} />
        {mode === 'search' ? (
          <TopicSearchPicker
            activePaths={activePaths}
            onSearch={paths => history.push(`/s?${stringify({ mode: 'topics', paths: pathsParam(paths) })}`)}
          />
        ) : null}
        {mode === 'search' && topicMatches.length ? (
          <div className={dirCss.popular} data-topic-matches={topicMatches.length}>
            <p className={dirCss.sub}>Matching topics</p>
            <ul className={css.topicMatchList}>
              {topicMatches.map(p => (
                <li key={p}>
                  <NamedLink name="SearchPage" to={{ search: `?${stringify({ mode: 'topics', paths: pathsParam([p]) })}` }} className={css.topicMatch} data-topic-match={p}>
                    {pathLabels(p).join(' › ')}
                  </NamedLink>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
        {mode === 'search' && !(kwTerm && searchSettled && !sortedListings.length) ? (
          <div className={css.browsePopular} data-browse-popular="true">
            <p className={css.browsePopularTitle}>Browse popular topics</p>
            <PopularTopics className={dirCss.popular} />
            <ul className={css.skillPills} data-teach-pills="true" aria-label="Learn a skill">
              {SKILL_CATEGORIES.map(c => (
                <li key={c.id}>
                  <NamedLink name="SearchPage" to={{ search: `?${stringify({ offer: 'skills', teaches: c.id })}` }} className={css.skillPill} data-teach-pill={c.id}>
                    <span aria-hidden="true">{c.emoji}</span> Learn: {c.label}
                  </NamedLink>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
        {mode === 'search' && kwTerm && searchSettled && !sortedListings.length ? (
          <SmartSearchEmpty query={String(kwTerm).trim()} />
        ) : null}
        {mode === 'search' && kwTerm && sortedListings.length ? (
          <h3 className={css.matchingTitle} data-matching-listeners={sortedListings.length}>Matching listeners</h3>
        ) : null}
        {resultsBar}
      </div>
      ) : null}
      {mode === 'topics' ? (
      <section className={dirCss.browseBox} data-browse-by-topic="true" data-browse-mode="topics" id="browse-topics">
        <div className={dirCss.browseHead}>
          <h2 className={dirCss.heading}>What would you like to talk about?</h2>
          <p className={dirCss.sub} data-browse-subline="true">Explore topics you love and meet friendly listeners who share your interests.</p>
          {activePaths.length || activeTopics.length ? (
            <NamedLink
              name="SearchPage"
              to={{ search: stringify({ mode: 'listeners' }) }}
              className={dirCss.clear}
              data-browse-all="true"
            >
              Browse all listeners
            </NamedLink>
          ) : (
            <span className={dirCss.sub}>Tap + to open a category. Tick as many topics as you like.</span>
          )}
        </div>
        <TopicSearchPicker
          activePaths={activePaths}
          onSearch={paths =>
            history.push(`/s?${stringify({ ...search, mode: 'topics', cat: undefined, paths: pathsParam(paths), topics: undefined, view: undefined, page: undefined })}`)
          }
        />
        {firstActive && !groupAll ? (
          <p className={css.topicSaveRow} data-topic-save={firstActive}>
            <PathHeart path={firstActive} /> <span>Save this topic to your dashboard</span>
          </p>
        ) : null}
        <PopularTopics className={dirCss.popular} activePath={firstActive} linkMode="topics" />
        {!activePaths.length ? (
          <p className={dirCss.sub} data-live-count={liveCount} role="status" aria-live="polite">
            {liveCount === 1 ? '1 listener matches' : `${liveCount} listeners match`}
          </p>
        ) : null}
        {activePaths.length ? (
              <div className={css.inlineMatches} data-inline-matches={liveCount}>
                {resultsBar}
                {liveCount ? (
                  <button
                    type="button"
                    className={css.seeMatches}
                    data-live-count={liveCount}
                    aria-live="polite"
                    onClick={() => {
                      const el = document.getElementById('browse-inline-results');
                      if (el) el.scrollIntoView({ block: 'start', behavior: 'smooth' });
                    }}
                  >
                    {liveCount === 1 ? 'See 1 listener →' : `See ${liveCount} listeners →`}
                  </button>
                ) : (
                  <div data-topic-fallback="true">
                    <p className={css.topicFallbackTitle} role="status" data-live-count={0}>
                      No exact match yet, but these caring listeners are close to what you picked.
                    </p>
                    <ul className={classNames(css.inlineList, css.browseGrid)}>
                      {(() => {
                        const near = rankListingsByPaths(discoveryListings, activePaths, presenceByListingId, 6).map(r => r.listing);
                        const list = near.length ? near : discoveryListings.slice(0, 6);
                        return list.map(l => (
                          <li key={l.id.uuid} className={css.resultItem}>
                            <ListenerBrowseCard
                              listing={l}
                              reviewStats={reviewStatsByListingId[l.id.uuid]}
                              online={liveOnline(l)}
                  away={awayFor(l)}
                              sessionBusy={!!busyMap[l.id.uuid]}
                              startNowPaused={!!pausedMap[l.id.uuid]}
                              nextAvailableAt={nextAvailableMap[l.id.uuid] || null}
                            />
                          </li>
                        ));
                      })()}
                    </ul>
                    <NamedLink name="SearchPage" to={{ search: '?mode=listeners' }} className={dirCss.clear}>Browse all listeners</NamedLink>
                  </div>
                )}
                {liveCount ? (
                  <ul className={classNames(css.inlineList, css.browseGrid)} id="browse-inline-results">
                    {sortedListings.map(l => (
                      <li key={l.id.uuid} className={css.resultItem}>
                        <ListenerBrowseCard
                          listing={l}
                          reviewStats={reviewStatsByListingId[l.id.uuid]}
                          online={liveOnline(l)}
                  away={awayFor(l)}
                          sessionBusy={!!busyMap[l.id.uuid]}
                          startNowPaused={!!pausedMap[l.id.uuid]}
                          nextAvailableAt={nextAvailableMap[l.id.uuid] || null}
                        />
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>
            ) : null}
      </section>
      ) : null}
      {mode === 'topics' && !activePaths.length && !activeTopics.length ? <MatchRow rows={matchRows} /> : null}
      {onlineOnly && sortedListings.length > 0 ? (
        <p className={css.onlineFilterNote}>
          <FormattedMessage
            id="SearchResultsPanel.onlineNowNote"
            values={{ count: sortedListings.length }}
          />
        </p>
      ) : null}
      {onlineOnly && sortedListings.length === 0 ? (
        <div className={css.emptyOnline} role="status">
          <p className={css.emptyOnlineText}>
            <FormattedMessage id="SearchResultsPanel.noOnlineNow" />
          </p>
          <NamedLink
            className={css.anyTimeCta}
            name={pageName}
            params={listingTypeParam ? { listingType: listingTypeParam } : {}}
            to={{
              search: `?${stringify({
                ...search,
                page: undefined,
                [AVAILABLE_NOW_PARAM]: availableNowParamForAnyTime(),
              })}`,
            }}
          >
            <FormattedMessage id="SearchResultsPanel.showAnyTime" />
          </NamedLink>
        </div>
      ) : null}
      {(womanOn || ageOn) ? (
        <p className={css.listenerFilterNote} data-listener-filter-note="true">
          Showing listeners who chose to share {womanOn && ageOn ? 'their gender and age range' : womanOn ? 'that they’re a woman' : 'their age range'}.
        </p>
      ) : null}
      {firstActive && !sortedListings.length ? (
        <div className={css.topicEmpty} role="status" data-topic-empty="true">
          <p className={css.topicEmptyTitle}>No listeners for {emptyLabel} yet.</p>
          {nearest ? (
            <p className={css.topicEmptyBody} data-topic-nearest={nearest.parent}>
              Here are listeners who talk about {nearest.label}:
            </p>
          ) : (
            <p className={css.topicEmptyBody}>Try a broader topic, or browse everyone.</p>
          )}
          <div className={css.topicEmptyActions}>
            <NamedLink name="SearchPage" to={{ search: '?mode=listeners' }} className={css.topicEmptyLink} data-topic-empty-all="true">
              Browse all listeners
            </NamedLink>
            <button
              type="button"
              className={css.topicEmptyLink}
              data-topic-empty-suggest="true"
              onClick={() => {
                const b = document.querySelector('[data-browse-by-topic] [data-suggest-topic]');
                if (b) {
                  b.scrollIntoView({ block: 'center' });
                  b.click();
                }
              }}
            >
              Suggest a topic
            </button>
          </div>
        </div>
      ) : null}
      {nearest ? (
      <ul className={classNames(isMapVariant ? css.listingCardsMapVariant : css.listingCards, css.browseGrid)} data-topic-nearest-list="true">
        {nearest.rows.map(l => (
          <li key={l.id.uuid} className={css.resultItem}>
            <ListenerBrowseCard
              listing={l}
              reviewStats={reviewStatsByListingId[l.id.uuid]}
              online={liveOnline(l)}
                  away={awayFor(l)}
              sessionBusy={!!busyMap[l.id.uuid]}
              startNowPaused={!!pausedMap[l.id.uuid]}
              nextAvailableAt={nextAvailableMap[l.id.uuid] || null}
            />
          </li>
        ))}
      </ul>
      ) : null}
      {/* Topics mode: no generic grid until a topic is picked; picked results show inline (no duplicates). */}
      {(mode === 'topics' && (activePaths.length || !activeTopics.length)) || searchEmpty ? null : (
      <ul className={classNames(isMapVariant ? css.listingCardsMapVariant : css.listingCards, css.browseGrid)}>
        {sortedListings.map(l => (
          <li key={l.id.uuid} className={css.resultItem}>
            <ListenerBrowseCard
              listing={l}
              reviewStats={reviewStatsByListingId[l.id.uuid]}
              online={liveOnline(l)}
                  away={awayFor(l)}
              sessionBusy={!!busyMap[l.id.uuid]}
              startNowPaused={!!pausedMap[l.id.uuid]}
              nextAvailableAt={nextAvailableMap[l.id.uuid] || null}
            />
          </li>
        ))}
        {props.children}
      </ul>
      )}
      {paginationLinks}
    </div>
  );
};

export default SearchResultsPanel;
